import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import styles from './Schedules.module.css'
import rp from '../ReportsPage.module.css'
import { apiJson } from '../../lib/api'
import { PendingButton, Failed } from '../../components/pending'
import { dmy } from '../wages/format'
import type { ReportDef } from './model'
import { OUTCOMES, cadenceLabel, lastSentText, openedText, plural, recipientsText, sendTime, runNote, scheduleSummary, scheduleTitleLine, whenText, type Run, type Schedule } from './schedules'

// Reports home, Scheduled delivery (handoff §7l.6): every schedule in the
// workspace, the one place they all live. Each row says what goes where and
// when it last ran; Pause/Resume, Edit and Revoke link act on it; Delivery
// history, collapsed by default, answers "did the grower get Monday's?".

type User = Parameters<typeof apiJson>[0]

interface Props {
  user: User
  schedules: Schedule[]
  timezone: string | null
  reports: ReportDef[]
  onChanged: (s: Schedule) => void
  onFailed: (e: unknown) => void
  onEdit: (s: Schedule) => void
  toast: (m: string) => void
  // shared_reports (D1.5): Edit, Pause/Resume and Revoke link are for ADMIN
  // and SYSADMIN; everyone else sees the schedules and their history.
  canManage?: boolean
}

export function ScheduledDelivery({ user, schedules, timezone, reports, onChanged, onFailed, onEdit, toast, canManage = true }: Props) {
  const [open, setOpen] = useState<string | null>(null)
  const [runs, setRuns] = useState<Record<string, Run[] | 'loading' | { error: string }>>({})
  // Which row's write is out, and what it is.
  const [busy, setBusy] = useState<{ id: string; kind: 'toggle' | 'revoke' } | null>(null)
  const [errs, setErrs] = useState<Record<string, string | null>>({})

  const keysOf = (s: Schedule) => reports.find((r) => r.key === s.report_key)?.filters || Object.keys(s.filters)

  const loadRuns = async (s: Schedule) => {
    setRuns((x) => ({ ...x, [s.id]: 'loading' }))
    try {
      const r = await apiJson<{ runs: Run[] }>(user, `/api/report-schedules/${s.id}/runs`)
      setRuns((x) => ({ ...x, [s.id]: r.runs }))
    } catch (e: any) {
      setRuns((x) => ({ ...x, [s.id]: { error: e?.message || 'Could not load the delivery history' } }))
    }
  }
  const toggleHistory = (s: Schedule) => {
    if (open === s.id) return setOpen(null)
    setOpen(s.id)
    loadRuns(s)
  }

  const write = async (s: Schedule, kind: 'toggle' | 'revoke') => {
    if (busy) return
    setBusy({ id: s.id, kind })
    setErrs((x) => ({ ...x, [s.id]: null }))
    try {
      if (kind === 'toggle') {
        const r = await apiJson<{ schedule: Schedule }>(user, `/api/report-schedules/${s.id}`, { method: 'PUT', body: { version: s.version, enabled: !s.enabled } })
        onChanged(r.schedule)
        toast(r.schedule.enabled ? `${r.schedule.report_name} resumed. It sends again from tonight` : `${r.schedule.report_name} paused. Nothing sends until it is resumed`)
      } else {
        const r = await apiJson<{ schedule: Schedule; revoked_links: number }>(user, `/api/report-schedules/${s.id}/revoke`, { method: 'POST', body: { version: s.version } })
        onChanged(r.schedule)
        toast('Link revoked. Anyone holding the old one now gets nothing. The next send carries a new link')
      }
    } catch (e: any) {
      if (e?.body?.code === 'STALE' || e?.status === 404) onFailed(e)
      else setErrs((x) => ({ ...x, [s.id]: e?.message || 'Could not change the schedule' }))
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className={rp.section} aria-label="Scheduled delivery">
      <div className={rp.viewsHead}>
        <span className={rp.eyebrow}>Scheduled delivery</span>
        <span className={rp.viewsSub} data-testid="schedule-summary">
          {scheduleSummary(schedules)}
        </span>
      </div>
      {!timezone && (
        <p className={styles.tzNote}>
          Schedules send at {sendTime(timezone)} until the operating time zone is set in <Link to="/settings">General settings</Link>.
        </p>
      )}
      <div className={styles.list}>
        {schedules.map((s) => {
          const h = runs[s.id]
          const rowBusy = busy?.id === s.id
          return (
            <div key={s.id} className={styles.row} data-testid="schedule-row">
              <div className={styles.rowHead}>
                <div className={styles.rowText}>
                  <div className={styles.rowTitle}>
                    <span className={styles.rowName}>{scheduleTitleLine(s, keysOf(s))}</span>
                    <span className={`${styles.state} ${s.enabled ? styles.stateOn : styles.stateOff}`}>{s.enabled ? 'Active' : 'Paused'}</span>
                  </div>
                  <div className={styles.rowMeta}>
                    {cadenceLabel(s.cadence)} · {recipientsText(s)}
                    {s.only_if_activity ? ' · only when there was activity' : ''}
                  </div>
                  <div className={styles.rowMeta2}>
                    Link expires after {s.link_expiry} · {lastSentText(s)} · set up by {s.created_by}
                  </div>
                </div>
                <button type="button" className={styles.smallBtn} aria-expanded={open === s.id} onClick={() => toggleHistory(s)}>
                  {open === s.id ? 'Hide history' : 'Delivery history'}
                </button>
                {canManage && (
                  <>
                    <button type="button" className={styles.smallBtn} onClick={() => onEdit(s)} disabled={rowBusy}>
                      Edit
                    </button>
                    <PendingButton type="button" className={styles.smallBtn} onClick={() => write(s, 'toggle')} disabled={!!busy} busy={rowBusy && busy?.kind === 'toggle'} pending={s.enabled ? 'Pausing…' : 'Resuming…'}>
                      {s.enabled ? 'Pause' : 'Resume'}
                    </PendingButton>
                    <PendingButton type="button" className={`${styles.smallBtn} ${styles.revokeBtn}`} onClick={() => write(s, 'revoke')} disabled={!!busy} busy={rowBusy && busy?.kind === 'revoke'} pending="Revoking…">
                      Revoke link
                    </PendingButton>
                  </>
                )}
              </div>
              {errs[s.id] && (
                <div className={styles.rowError}>
                  <Failed text={errs[s.id]} />
                </div>
              )}
              {open === s.id && (
                <div className={styles.history} aria-label={`Delivery history for ${s.report_name}`}>
                  {h === 'loading' || h === undefined ? (
                    <div className={styles.historyEmpty}>Loading…</div>
                  ) : 'error' in (h as object) ? (
                    <div className={styles.historyEmpty}>{(h as { error: string }).error}</div>
                  ) : (h as Run[]).length === 0 ? (
                    <div className={styles.historyEmpty}>Not sent yet. The first run is at {sendTime(timezone)}.</div>
                  ) : (
                    (h as Run[]).map((r) => {
                      const o = OUTCOMES[r.outcome]
                      const note = runNote(r)
                      return (
                        <div key={r.id} className={styles.run} data-testid="schedule-run">
                          <span className={styles.runAt}>{whenText(r.ran_at)}</span>
                          <span className={styles.runPill} style={{ background: o.bg, color: o.fg }}>
                            {o.label}
                          </span>
                          {r.recipient_count !== null && <span className={styles.runTo}>{plural(r.recipient_count, 'recipient')}</span>}
                          {r.period_from && (
                            <span className={styles.runPeriod}>{r.period_from === r.period_to ? dmy(r.period_from) : `${dmy(r.period_from)} – ${dmy(r.period_to)}`}</span>
                          )}
                          <span className={styles.runOpened}>{openedText(r)}</span>
                          {note && <span className={styles.runNote}>{note}</span>}
                        </div>
                      )
                    })
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}
